/**
 * Prozedurale Figuren — „Tinte & Flächen".
 *
 * Wir zeichnen nicht, wir bauen Erzeuger (docs/design/art-direction.md §5.1).
 * Jede Figur ist ein Gliederpuppen-Skelett aus wenigen Segmenten; Animation ist
 * Kinematik über der Zeit, keine gezeichneten Einzelbilder. Eine neue Gegnerart
 * ist damit eine JSON-Zeile, kein Zeichenauftrag.
 *
 * Regeln, die hier eingehalten werden:
 *   - schwere, ungleichmäßige Kontur (dicker unten, dünner oben)
 *   - höchstens drei flache Füllungen je Figur, nie Verläufe
 *   - Tiefe entsteht durch Schraffur, nie durch Weichzeichnung
 */

export interface Farben {
  tinte: string;
  papier: string;
  /** Grundton einer Figurenfläche. */
  flaeche: string;
  /** Zweite, dunklere Fläche — Schatten und abgesetzte Teile. */
  schatten: string;
  /** Der EINE Akzent. Nur Licht, Feuer, Gefahr und Wertvolles. */
  glut: string;
  /** Geerdeter Wert-Ton (Zunft, Gold). Nie als Verlauf. */
  messing: string;
  /** Kälte, Wasser, Frost. */
  span: string;
  /** Fels — die undurchdringliche Masse. Muss deutlich dunkler sein als der Boden,
   *  sonst entsteht kein Holzschnitt-Kontrast, sondern Matsch. */
  fels: string;
  /** Bodenton der Wahrzeichen-Räume. */
  wahrzeichen: string;
}

export type Grundform = 'kaefer' | 'klumpen' | 'flatterer' | 'gebeugt' | 'aufrecht' | 'held';

export interface FigurLage {
  /** Bildschirmposition des Fußpunkts. */
  x: number;
  y: number;
  /** Pixel je Welteinheit. */
  masstab: number;
  /** Blickrichtung in Radiant. */
  winkel: number;
  /** Laufende Zeit in Sekunden — treibt den Schrittzyklus. */
  zeit: number;
  /** 0 = steht, 1 = läuft. */
  bewegung: number;
  /** 0..1 Fortschritt einer Wirkanimation. */
  aktion: number;
  hoehe: number;
  breite: number;
  segmente: number;
}

/** Ungleichmäßige Kontur: dicker unten, dünner oben. Gleichmäßig sieht nach Klipart aus. */
function kontur(ctx: CanvasRenderingContext2D, staerke: number): void {
  ctx.lineWidth = staerke;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
}

function pfadEllipse(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  drehung = 0,
): void {
  ctx.beginPath();
  ctx.ellipse(x, y, Math.abs(rx), Math.abs(ry), drehung, 0, Math.PI * 2);
}

/** Flache Füllung plus Kontur — der Grundbaustein jeder Form. */
function fuelleUndZiehe(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  fuellung: string,
  staerke: number,
): void {
  ctx.fillStyle = fuellung;
  ctx.fill();
  kontur(ctx, staerke);
  ctx.strokeStyle = f.tinte;
  ctx.stroke();
}

/** Schraffur in einem Kreisausschnitt — unsere einzige Textur. */
function schraffiere(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  x: number,
  y: number,
  rx: number,
  ry: number,
  dichte: number,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(x, y, Math.abs(rx), Math.abs(ry), 0, 0, Math.PI * 2);
  ctx.clip();
  ctx.strokeStyle = f.tinte;
  ctx.globalAlpha = 0.28;
  ctx.lineWidth = Math.max(0.6, rx * 0.06);
  const schritt = Math.max(2.2, rx / dichte);
  ctx.beginPath();
  for (let i = -rx * 2; i < rx * 2; i += schritt) {
    ctx.moveTo(x + i, y - ry);
    ctx.lineTo(x + i + ry * 1.1, y + ry);
  }
  ctx.stroke();
  ctx.restore();
}

/** Ein Bein als zweigliedriger Strich mit Schrittzyklus. */
function bein(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  huefteX: number,
  huefteY: number,
  laenge: number,
  phase: number,
  bewegung: number,
  staerke: number,
): void {
  const schwung = Math.sin(phase) * 0.55 * bewegung;
  const heben = Math.max(0, Math.cos(phase)) * 0.28 * bewegung;
  const knieX = huefteX + Math.sin(schwung) * laenge * 0.5;
  const knieY = huefteY + Math.cos(schwung) * laenge * 0.5;
  const fussX = knieX + Math.sin(schwung * 0.4) * laenge * 0.5;
  const fussY = knieY + laenge * 0.5 * (1 - heben);

  ctx.beginPath();
  ctx.moveTo(huefteX, huefteY);
  ctx.lineTo(knieX, knieY);
  ctx.lineTo(fussX, fussY);
  kontur(ctx, staerke);
  ctx.strokeStyle = f.tinte;
  ctx.stroke();
}

/**
 * Zeichnet eine Figur. Der Ursprung ist der Fußpunkt; die Figur wächst nach oben.
 * `winkel` wird nur als leichte Neigung und Spiegelung benutzt — die Ansicht ist
 * schräg von oben, keine echte Rotation.
 */
export function zeichneFigur(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  form: Grundform,
  lage: FigurLage,
): void {
  const m = lage.masstab;
  const h = lage.hoehe * m;
  const b = lage.breite * m;
  const staerke = Math.max(1.2, m * 0.055);
  const phase = lage.zeit * 9 + lage.x * 0.07;
  const blicktLinks = Math.cos(lage.winkel) < 0;

  ctx.save();
  ctx.translate(lage.x, lage.y);
  if (blicktLinks) ctx.scale(-1, 1);

  // Bodenschatten: eine flache Ellipse, kein Weichzeichner.
  ctx.globalAlpha = 0.16;
  pfadEllipse(ctx, 0, 0, b * 0.5, b * 0.18);
  ctx.fillStyle = f.tinte;
  ctx.fill();
  ctx.globalAlpha = 1;

  // Leichtes Wippen beim Laufen — das verkauft die Bewegung mehr als alles andere.
  const wippen = Math.abs(Math.sin(phase)) * h * 0.035 * lage.bewegung;
  ctx.translate(0, -wippen);

  switch (form) {
    case 'kaefer':
      zeichneKaefer(ctx, f, h, b, phase, lage, staerke);
      break;
    case 'klumpen':
      zeichneKlumpen(ctx, f, h, b, phase, lage, staerke);
      break;
    case 'flatterer':
      zeichneFlatterer(ctx, f, h, b, phase, lage, staerke);
      break;
    case 'gebeugt':
      zeichneGebeugt(ctx, f, h, b, phase, lage, staerke);
      break;
    case 'aufrecht':
      zeichneAufrecht(ctx, f, h, b, phase, lage, staerke);
      break;
    case 'held':
      zeichneHeld(ctx, f, h, b, phase, lage, staerke);
      break;
  }

  ctx.restore();
}

// ------------------------------------------------------------------ Formen

function zeichneKaefer(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  h: number,
  b: number,
  phase: number,
  lage: FigurLage,
  staerke: number,
): void {
  const koerperY = -h * 0.55;
  // Sechs kurze Beine, paarweise versetzt.
  for (let i = 0; i < 3; i++) {
    const x = (-0.3 + i * 0.3) * b;
    bein(ctx, f, x, koerperY + h * 0.15, h * 0.42, phase + i * 2.1, lage.bewegung, staerke * 0.8);
    bein(ctx, f, x, koerperY + h * 0.15, h * 0.42, phase + i * 2.1 + Math.PI, lage.bewegung, staerke * 0.8);
  }
  // Panzer
  pfadEllipse(ctx, 0, koerperY, b * 0.5, h * 0.45);
  fuelleUndZiehe(ctx, f, f.flaeche, staerke);
  schraffiere(ctx, f, b * 0.12, koerperY + h * 0.08, b * 0.36, h * 0.34, 5);
  // Kopf als Keil
  ctx.beginPath();
  ctx.moveTo(b * 0.42, koerperY - h * 0.06);
  ctx.lineTo(b * 0.72, koerperY + h * 0.02);
  ctx.lineTo(b * 0.42, koerperY + h * 0.14);
  ctx.closePath();
  fuelleUndZiehe(ctx, f, f.schatten, staerke * 0.9);
}

function zeichneKlumpen(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  h: number,
  b: number,
  phase: number,
  lage: FigurLage,
  staerke: number,
): void {
  // Zwei stumpfe Stummel statt Beine
  bein(ctx, f, -b * 0.2, -h * 0.3, h * 0.3, phase, lage.bewegung * 0.6, staerke);
  bein(ctx, f, b * 0.2, -h * 0.3, h * 0.3, phase + Math.PI, lage.bewegung * 0.6, staerke);

  // Unregelmaessiger Klumpen aus drei ueberlappenden Formen
  const q = Math.sin(phase * 0.5) * 0.03;
  pfadEllipse(ctx, 0, -h * 0.5, b * 0.52, h * 0.44 * (1 + q));
  fuelleUndZiehe(ctx, f, f.flaeche, staerke);
  pfadEllipse(ctx, -b * 0.18, -h * 0.72, b * 0.3, h * 0.26);
  fuelleUndZiehe(ctx, f, f.flaeche, staerke * 0.85);
  pfadEllipse(ctx, b * 0.22, -h * 0.38, b * 0.26, h * 0.24);
  fuelleUndZiehe(ctx, f, f.schatten, staerke * 0.85);
  schraffiere(ctx, f, -b * 0.1, -h * 0.5, b * 0.4, h * 0.34, 4);

  // Glutriss — das einzige Leuchten
  ctx.beginPath();
  ctx.moveTo(-b * 0.24, -h * 0.52);
  ctx.lineTo(b * 0.02, -h * 0.42);
  ctx.lineTo(-b * 0.08, -h * 0.62);
  ctx.strokeStyle = f.glut;
  ctx.lineWidth = staerke * 1.1;
  ctx.stroke();
}

function zeichneFlatterer(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  h: number,
  b: number,
  phase: number,
  _lage: FigurLage,
  staerke: number,
): void {
  const schweben = Math.sin(phase * 1.6) * h * 0.08;
  const koerperY = -h * 0.7 + schweben;
  const fluegel = 0.35 + Math.abs(Math.sin(phase * 2.4)) * 0.5;

  // Fluegel als zwei Boegen
  for (const seite of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(0, koerperY);
    ctx.quadraticCurveTo(
      seite * b * 0.75,
      koerperY - h * 0.5 * fluegel,
      seite * b * 0.55,
      koerperY + h * 0.18,
    );
    ctx.closePath();
    ctx.fillStyle = f.schatten;
    ctx.globalAlpha = 0.8;
    ctx.fill();
    ctx.globalAlpha = 1;
    kontur(ctx, staerke * 0.85);
    ctx.strokeStyle = f.tinte;
    ctx.stroke();
  }

  pfadEllipse(ctx, 0, koerperY, b * 0.16, h * 0.28);
  fuelleUndZiehe(ctx, f, f.flaeche, staerke);

  // Glutpunkt
  pfadEllipse(ctx, 0, koerperY - h * 0.12, b * 0.07, b * 0.07);
  ctx.fillStyle = f.glut;
  ctx.fill();
}

function zeichneGebeugt(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  h: number,
  b: number,
  phase: number,
  lage: FigurLage,
  staerke: number,
): void {
  bein(ctx, f, -b * 0.16, -h * 0.42, h * 0.42, phase, lage.bewegung, staerke);
  bein(ctx, f, b * 0.16, -h * 0.42, h * 0.42, phase + Math.PI, lage.bewegung, staerke);

  // Gebeugter Rumpf
  ctx.beginPath();
  ctx.moveTo(-b * 0.3, -h * 0.4);
  ctx.quadraticCurveTo(-b * 0.42, -h * 0.85, b * 0.1, -h * 0.9);
  ctx.quadraticCurveTo(b * 0.4, -h * 0.8, b * 0.3, -h * 0.42);
  ctx.closePath();
  fuelleUndZiehe(ctx, f, f.flaeche, staerke);
  schraffiere(ctx, f, -b * 0.06, -h * 0.62, b * 0.3, h * 0.24, 4);

  // Langer Arm, der beim Wirken vorschnellt
  const aus = lage.aktion * b * 0.5;
  ctx.beginPath();
  ctx.moveTo(b * 0.18, -h * 0.72);
  ctx.quadraticCurveTo(b * 0.5 + aus, -h * 0.6, b * 0.62 + aus, -h * 0.3);
  kontur(ctx, staerke);
  ctx.strokeStyle = f.tinte;
  ctx.stroke();

  // Kopf, tief zwischen den Schultern
  pfadEllipse(ctx, b * 0.16, -h * 0.92, b * 0.17, h * 0.13);
  fuelleUndZiehe(ctx, f, f.schatten, staerke * 0.9);
}

function zeichneAufrecht(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  h: number,
  b: number,
  phase: number,
  lage: FigurLage,
  staerke: number,
): void {
  bein(ctx, f, -b * 0.18, -h * 0.45, h * 0.45, phase, lage.bewegung, staerke * 1.1);
  bein(ctx, f, b * 0.18, -h * 0.45, h * 0.45, phase + Math.PI, lage.bewegung, staerke * 1.1);

  // Rumpf
  ctx.beginPath();
  ctx.moveTo(-b * 0.3, -h * 0.44);
  ctx.lineTo(-b * 0.36, -h * 0.82);
  ctx.quadraticCurveTo(0, -h * 0.94, b * 0.36, -h * 0.82);
  ctx.lineTo(b * 0.3, -h * 0.44);
  ctx.closePath();
  fuelleUndZiehe(ctx, f, f.flaeche, staerke);
  schraffiere(ctx, f, b * 0.1, -h * 0.62, b * 0.26, h * 0.2, 4);

  // Kessel auf dem Rücken
  pfadEllipse(ctx, -b * 0.34, -h * 0.72, b * 0.22, h * 0.2);
  fuelleUndZiehe(ctx, f, f.schatten, staerke * 0.9);

  // Kopf
  pfadEllipse(ctx, 0, -h * 0.98, b * 0.19, h * 0.1);
  fuelleUndZiehe(ctx, f, f.flaeche, staerke * 0.9);

  // Arm mit Ausholbewegung
  const aus = Math.sin(lage.aktion * Math.PI) * 1.1;
  ctx.beginPath();
  ctx.moveTo(b * 0.3, -h * 0.78);
  ctx.lineTo(b * 0.55 + aus * b * 0.3, -h * (0.62 - aus * 0.14));
  kontur(ctx, staerke * 1.15);
  ctx.strokeStyle = f.tinte;
  ctx.stroke();
}

function zeichneHeld(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  h: number,
  b: number,
  phase: number,
  lage: FigurLage,
  staerke: number,
): void {
  bein(ctx, f, -b * 0.17, -h * 0.42, h * 0.44, phase, lage.bewegung, staerke * 1.05);
  bein(ctx, f, b * 0.17, -h * 0.42, h * 0.44, phase + Math.PI, lage.bewegung, staerke * 1.05);

  // Breiter Rumpf — der Kesselschmied ist gedrungen, nicht heroisch schlank.
  ctx.beginPath();
  ctx.moveTo(-b * 0.32, -h * 0.42);
  ctx.lineTo(-b * 0.4, -h * 0.78);
  ctx.quadraticCurveTo(0, -h * 0.9, b * 0.4, -h * 0.78);
  ctx.lineTo(b * 0.32, -h * 0.42);
  ctx.closePath();
  fuelleUndZiehe(ctx, f, f.flaeche, staerke * 1.1);

  // Schürze als zweite Fläche
  ctx.beginPath();
  ctx.moveTo(-b * 0.24, -h * 0.6);
  ctx.lineTo(b * 0.24, -h * 0.6);
  ctx.lineTo(b * 0.2, -h * 0.36);
  ctx.lineTo(-b * 0.2, -h * 0.36);
  ctx.closePath();
  fuelleUndZiehe(ctx, f, f.schatten, staerke * 0.85);

  // Kopf
  pfadEllipse(ctx, 0, -h * 0.92, b * 0.2, h * 0.1);
  fuelleUndZiehe(ctx, f, f.flaeche, staerke);

  // Hammer: holt aus und schlägt durch
  const schwung = Math.sin(lage.aktion * Math.PI);
  const winkel = -0.9 + schwung * 2.1;
  ctx.save();
  ctx.translate(b * 0.34, -h * 0.66);
  ctx.rotate(winkel);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(b * 0.62, 0);
  kontur(ctx, staerke * 1.2);
  ctx.strokeStyle = f.tinte;
  ctx.stroke();
  ctx.beginPath();
  ctx.rect(b * 0.56, -h * 0.09, b * 0.2, h * 0.18);
  fuelleUndZiehe(ctx, f, f.schatten, staerke);
  ctx.restore();
}

/** Der Krümel: klein, rußschwarz, Weidenkorb, Vogelbeine. */
export function zeichneKruemel(
  ctx: CanvasRenderingContext2D,
  f: Farben,
  lage: FigurLage,
  korbFuellung: number,
): void {
  const m = lage.masstab;
  const h = 0.62 * m;
  const b = 0.5 * m;
  const staerke = Math.max(1, m * 0.05);
  const phase = lage.zeit * 12;
  const blicktLinks = Math.cos(lage.winkel) < 0;

  ctx.save();
  ctx.translate(lage.x, lage.y);
  if (blicktLinks) ctx.scale(-1, 1);

  ctx.globalAlpha = 0.16;
  pfadEllipse(ctx, 0, 0, b * 0.42, b * 0.16);
  ctx.fillStyle = f.tinte;
  ctx.fill();
  ctx.globalAlpha = 1;

  // Vogelbeine — duenn, hoch angesetzt, schneller Takt
  bein(ctx, f, -b * 0.12, -h * 0.42, h * 0.42, phase, lage.bewegung, staerke * 0.7);
  bein(ctx, f, b * 0.12, -h * 0.42, h * 0.42, phase + Math.PI, lage.bewegung, staerke * 0.7);

  // Korb auf dem Rücken, füllt sich sichtbar
  ctx.beginPath();
  ctx.rect(-b * 0.5, -h * 0.86, b * 0.34, h * 0.4);
  fuelleUndZiehe(ctx, f, f.papier, staerke * 0.8);
  if (korbFuellung > 0) {
    ctx.beginPath();
    ctx.rect(-b * 0.5, -h * 0.86 + h * 0.4 * (1 - korbFuellung), b * 0.34, h * 0.4 * korbFuellung);
    ctx.fillStyle = f.glut;
    ctx.globalAlpha = 0.55;
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // Körper: gedrungen, kapuzenartig
  ctx.beginPath();
  ctx.moveTo(-b * 0.3, -h * 0.4);
  ctx.quadraticCurveTo(-b * 0.34, -h * 0.92, 0, -h * 0.95);
  ctx.quadraticCurveTo(b * 0.36, -h * 0.9, b * 0.3, -h * 0.4);
  ctx.closePath();
  fuelleUndZiehe(ctx, f, f.tinte, staerke);

  // Zwei Glutaugen — das Einzige, was an ihm leuchtet
  ctx.fillStyle = f.glut;
  pfadEllipse(ctx, b * 0.1, -h * 0.72, b * 0.05, b * 0.05);
  ctx.fill();
  pfadEllipse(ctx, b * 0.24, -h * 0.7, b * 0.045, b * 0.045);
  ctx.fill();

  ctx.restore();
}
